import * as React from 'react';
import {
    CodeRejet,
    CodeRejetApiObject,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { AxiosPromise } from 'axios';
import { NamedAxiosPromise } from '@europrocurement/flexy-components/types/NamedAxiosPromise';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import LabeledAsyncSwitch from './LabeledAsyncSwitch';

/**
 * AsyncSwitchRejectCodeBloquant Component
 *
 * A group of switches to manage the toggle states for different reject code fields.
 * Each switch triggers an API request to update the corresponding field dynamically.
 *
 * Props:
 * - `rejectCode`: The reject code object containing field values for toggles.
 * - `fetchData`: A Redux thunk function to refresh data after toggle updates.
 *
 * Features:
 * - Dynamically updates reject code fields (e.g., `manuel`, `bloquant`, `relanceAuto`).
 * - Supports handling multiple toggle switches with customizable actions.
 * - Includes additional buttons for contextual interactions (e.g., frequency selection).
 *
 * Example:
 * ```
 * <AsyncSwitchRejectCodeBloquant
 *   rejectCode={rejectCode}
 *   fetchData={fetchData}
 * />
 * ```
 */

type AsyncSwitchRejectCodeBloquantProps = {
    rejectCode: CodeRejet;
    fetchData: getDataThunkType<CodeRejetApiObject>;
    disabledToggles?: Partial<Record<'manuel' | 'bloquant' | 'relanceAuto', boolean>>;
};

const AsyncSwitchRejectCodeBloquant: React.FunctionComponent<AsyncSwitchRejectCodeBloquantProps> =
    function ({ rejectCode, fetchData, disabledToggles }: AsyncSwitchRejectCodeBloquantProps) {
        const xIdSociete = useSociete();
        const { codeRejetApi } = useAchatsService();
        /**
         * Dynamically updates a specific field in the reject code.
         * @param field - The field to update in the reject code object.
         */
        const updateRejectCodeField = <K extends keyof CodeRejet>(
            field: K,
        ): AxiosPromise | undefined => {
            if (!rejectCode.code) {
                return undefined;
            }

            return codeRejetApi.patchCodeRejetItem({
                code: rejectCode.code,
                codeRejetCodeRejetUpdate: {
                    [field]: !rejectCode[field],
                },
                xIdSociete,
            });
        };

        /**
         * Generates a toggle request for a specific reject code field.
         * @param field - The field to toggle.
         * @returns An array of NamedAxiosPromise for toggle requests.
         */
        const createToggleRequest = (field: keyof CodeRejet): Array<NamedAxiosPromise> => [
            {
                name: `updateRejectCode${field.charAt(0).toUpperCase() + field.slice(1)}`,
                promise: updateRejectCodeField(field) as AxiosPromise,
            },
        ];

        return (
            <LabeledAsyncSwitch
                key={`${rejectCode.code}-bloquant_${rejectCode.bloquant}`}
                requestsOnToggle={() => createToggleRequest('bloquant')}
                fetchData={fetchData}
                defaultChecked={rejectCode.bloquant}
                disabled={!!disabledToggles?.bloquant}
                validationModal
            />
        );
    };

export default AsyncSwitchRejectCodeBloquant;
